import { useState, useContext, useEffect } from 'react';
import api from '../lib/api';
import { AuthContext } from '../App';
import { useCampfireMode } from '../hooks/useCampfireMode';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { StyleSelector } from './StyleSelector';
import { toast } from 'sonner';
import { MessageSquare, ChevronDown } from 'lucide-react';
import { pickThinkingPhrase } from '../lib/thinkingPhrases';

const QUICK_NAMES = ['baby', 'honey', 'sweetheart', 'champ', 'boss', 'kid', 'bud', 'darling', 'princess', 'love', 'dear', 'fam', 'bro', 'sis'];
const MIN_THINKING_MS = 1200; // Never flash the thinking phase away — hold at least this long

const VentForm = ({ activeConversation, onResult, onCrisisDetected, initialStyle, initialPlaceholder }) => {
  const { user } = useContext(AuthContext);
  const campfire = useCampfireMode();
  const [ventText, setVentText] = useState('');
  const [selectedStyle, setSelectedStyle] = useState(initialStyle || 'Dad');
  const [callMe, setCallMe] = useState(user?.nickname || user?.name || 'friend');
  const [showNamePicker, setShowNamePicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thinkingPhrase, setThinkingPhrase] = useState('');

  const defaultPlaceholder = campfire
    ? "Pull up a seat by the fire. What's on your mind tonight?"
    : "Share your thoughts, feelings, or what's troubling you... There's no judgment here.";

  useEffect(() => {
    if (initialStyle) setSelectedStyle(initialStyle);
  }, [initialStyle]);

  const CRISIS_KEYWORDS = [
    'suicide', 'kill myself', 'end my life', 'want to die', 'better off dead',
    'self harm', 'hurt myself', 'cut myself', 'no reason to live', 'suicide plan',
    'overdose', 'jump off', 'hang myself', 'shoot myself'
  ];

  const handleTextChange = (e) => {
    const text = e.target.value;
    setVentText(text);
    const lowerText = text.toLowerCase();
    const hasCrisis = CRISIS_KEYWORDS.some(keyword => lowerText.includes(keyword));
    onCrisisDetected(hasCrisis);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ventText.trim()) {
      toast.error('Please enter your thoughts');
      return;
    }
    setIsSubmitting(true);
    setThinkingPhrase(pickThinkingPhrase());
    // Rotate phrase every 2s while thinking
    const rotator = setInterval(() => setThinkingPhrase(pickThinkingPhrase()), 2000);
    const startedAt = Date.now();
    try {
      const response = await api.post('/vent', {
        text: ventText,
        response_style: selectedStyle,
        conversation_id: activeConversation?.conversation_id || null,
        call_me: callMe || 'friend'
      });
      // Ensure the thinking phase is felt (never flashes away)
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_THINKING_MS) {
        await new Promise((r) => setTimeout(r, MIN_THINKING_MS - elapsed));
      }
      onResult(response.data);
      setVentText('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') toast.error(detail);
      else if (Array.isArray(detail)) toast.error(detail.map(d => d.msg || '').join(', '));
      else toast.error('Failed to process your message');
    } finally {
      clearInterval(rotator);
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="card-surface border-[#27272A] mb-8" data-testid="vent-form-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <MessageSquare className="w-5 h-5 text-[#8B5CF6]" />
          What's on your mind?
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Textarea
            value={ventText}
            onChange={handleTextChange}
            placeholder={initialPlaceholder || defaultPlaceholder}
            rows={8}
            className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6] p-6 text-base leading-relaxed resize-none"
            data-testid="vent-textarea"
          />

          <div data-testid="call-me-picker">
            <button
              type="button"
              onClick={() => setShowNamePicker(!showNamePicker)}
              className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#F9FAFB] transition-colors"
              data-testid="call-me-toggle"
            >
              <span>Call me:</span>
              <span className="text-[#8B5CF6] font-semibold">{callMe}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showNamePicker ? 'rotate-180' : ''}`} />
            </button>

            {showNamePicker && (
              <div className="mt-3 space-y-2">
                <Input
                  value={callMe}
                  onChange={(e) => setCallMe(e.target.value)}
                  placeholder="Type your name or pick one"
                  className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#8B5CF6] text-sm"
                  data-testid="call-me-input"
                />
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_NAMES.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => { setCallMe(n); setShowNamePicker(false); }}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${callMe === n ? 'bg-[#8B5CF6] border-[#8B5CF6] text-white' : 'bg-[#0A0A0A] border-[#27272A] text-[#A1A1AA] hover:border-[#8B5CF6]'}`}
                      data-testid={`call-me-${n}`}
                    >{n}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <StyleSelector selectedStyle={selectedStyle} onSelect={setSelectedStyle} />
          <Button type="submit" disabled={isSubmitting} className="btn-gold w-full rounded-full text-base py-6 font-semibold transition-all duration-500" data-testid="submit-vent-button">
            {isSubmitting ? (
              <span className="inline-flex items-center gap-2 italic font-normal" data-testid="vent-thinking-phrase">
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                {thinkingPhrase}
              </span>
            ) : (
              "I'm ready. Send it when you are."
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default VentForm;
